import { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SEO = ({ 
  title, 
  description, 
  keywords,
  canonical,
  schema,
  ogImage,
  ogUrl,
  pageRoute
}) => {
  const [overrides, setOverrides] = useState(null);

  useEffect(() => {
    if (pageRoute) {
      fetch(`${API_URL}/api/seo-settings/${encodeURIComponent(pageRoute.startsWith('/') ? pageRoute : '/' + pageRoute)}`)
        .then(res => res.ok ? res.json() : null)
        .then(data => { if (data) setOverrides(data); })
        .catch(() => {});
    }
  }, [pageRoute]);

  const siteTitle = "EQUIPR Software";
  const o = overrides || {};
  
  const finalTitle = o.meta_title || (title ? `${title} | ${siteTitle}` : `${siteTitle} | Plant & Mobile Crane Hire Software`);
  const finalDescription = o.meta_description || description || "EQUIPR Software - Operations software for plant and mobile crane hire. Streamline scheduling, dockets, and compliance.";
  const finalKeywords = o.meta_keywords || keywords;
  const finalCanonical = o.canonical_url || canonical;
  const finalOgTitle = o.og_title || finalTitle;
  const finalOgDescription = o.og_description || finalDescription;
  const finalOgImage = o.og_image || ogImage || "https://equiprsoftware.com/og-image.jpg";
  const finalOgUrl = o.og_url || ogUrl;
  
  const geoSummary = o.geo_ai_summary;
  const geoEntity = o.geo_entity_description;
  const geoFeatures = o.geo_key_features;
  const geoQueries = o.geo_target_queries;
  const geoCitations = o.geo_citations;

  return (
    <>
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      {finalKeywords && <meta name="keywords" content={finalKeywords} />}
      {finalCanonical && <link rel="canonical" href={finalCanonical} />}
      
      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={finalOgTitle} />
      <meta property="og:description" content={finalOgDescription} />
      <meta property="og:image" content={finalOgImage} />
      {finalOgUrl && <meta property="og:url" content={finalOgUrl} />}
      <meta property="og:site_name" content="EQUIPR Software" />
      <meta property="og:locale" content="en_AU" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalOgTitle} />
      <meta name="twitter:description" content={finalOgDescription} />
      <meta name="twitter:image" content={finalOgImage} />
      
      {/* GEO - Generative Engine Optimisation */}
      {geoSummary && <meta name="ai-content-summary" content={geoSummary} />}
      {geoEntity && <meta name="entity-description" content={geoEntity} />}
      {geoFeatures && geoFeatures.length > 0 && (
        <meta name="ai-key-features" content={geoFeatures.join('; ')} />
      )}
      {geoQueries && geoQueries.length > 0 && (
        <meta name="ai-target-queries" content={geoQueries.join('; ')} />
      )}
      {geoCitations && geoCitations.length > 0 && (
        <meta name="ai-citations" content={geoCitations.join(' | ')} />
      )}
      
      {/* Structured Data */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
      
      {/* GEO FAQ Schema from overrides (if any FAQ exists on the page) */}
      {geoSummary && geoEntity && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": finalTitle,
            "description": finalDescription,
            "url": finalOgUrl || finalCanonical,
            "about": {
              "@type": "SoftwareApplication",
              "name": "EQUIPR Software",
              "description": geoEntity,
              "applicationCategory": "BusinessApplication",
              "operatingSystem": "Web-based, iOS, Android",
              ...(geoFeatures && geoFeatures.length > 0 ? { "featureList": geoFeatures.join(', ') } : {})
            },
            "provider": {
              "@type": "Organization",
              "name": "EQUIPR Software",
              "url": "https://equiprsoftware.com"
            }
          })}
        </script>
      )}
    </>
  );
};

// Pre-built schema generators
export const schemas = {
  organization: {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "EQUIPR Software",
    "url": "https://equiprsoftware.com",
    "logo": "https://equiprsoftware.com/logo.png",
    "description": "Australia's leading operations management software for plant hire, mobile cranes, civil construction, and heavy equipment industries",
    "foundingCountry": "AU",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Melbourne",
      "addressRegion": "VIC",
      "addressCountry": "AU"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "sales",
      "email": "info@equiprsoftware.com"
    },
    "sameAs": [
      "https://www.linkedin.com/company/13664129",
      "https://www.facebook.com/profile.php?id=100076819572027",
      "https://www.instagram.com/equipr_software/"
    ]
  },
  
  website: {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "EQUIPR Software",
    "url": "https://equiprsoftware.com",
    "description": "Operations management software for plant hire, mobile cranes, and heavy equipment",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://equiprsoftware.com/search?q={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  },
  
  softwareApplication: (name, description, category) => ({
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": name,
    "description": description,
    "applicationCategory": category || "BusinessApplication",
    "operatingSystem": "Web-based, iOS, Android",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "AUD",
      "availability": "https://schema.org/InStock"
    },
    "provider": {
      "@type": "Organization",
      "name": "EQUIPR Software",
      "url": "https://equiprsoftware.com"
    }
  }),
  
  article: (title, description, datePublished, author, image) => ({
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": description,
    "datePublished": datePublished,
    "dateModified": datePublished,
    "author": {
      "@type": "Organization",
      "name": author || "EQUIPR Software"
    },
    "publisher": {
      "@type": "Organization",
      "name": "EQUIPR Software",
      "logo": {
        "@type": "ImageObject",
        "url": "https://equiprsoftware.com/logo.png"
      }
    },
    "image": image || "https://equiprsoftware.com/og-image.jpg"
  }),
  
  localBusiness: {
    "@context": "https://schema.org",
    "@type": "SoftwareCompany",
    "name": "EQUIPR Software",
    "description": "Australia's leading operations management software for plant hire, mobile cranes, and heavy equipment industries",
    "url": "https://equiprsoftware.com",
    "email": "info@equiprsoftware.com",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Melbourne",
      "addressRegion": "VIC",
      "addressCountry": "AU"
    },
    "priceRange": "$$",
    "areaServed": {
      "@type": "Country",
      "name": "Australia"
    }
  },
  
  faq: (questions) => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": questions.map(q => ({
      "@type": "Question",
      "name": q.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": typeof q.answer === 'string' ? q.answer : ''
      }
    }))
  }),
  
  breadcrumb: (items) => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  })
};

export default SEO;
